import {AsyncCollectionConcurrentFetchingAction} from '../async-collection/async-collection';
import {AsyncListBase} from '../async-list-base/async-list-base';
import {evaluateFunction} from "../evaluable";
import {Deferred} from "../async-utils/async-utils";
import {AsyncListFetchResult} from "../async-list/async-list";
import {arrayEqual, arraySkipTake} from "../array-utils";

export namespace AsyncListWithSkipTake {
    export interface FetchResult<T> extends AsyncListFetchResult<T> {
        count: number;
    }

    export type FetchResultWithNullableTotalCount<T> = Omit<FetchResult<T>, 'count'> & {
        count: number | undefined;
    }

    export interface AsyncListStructure<T> {
        items: T[];
        totalCount: number | undefined;
        cursor: number;
        hasMore: boolean;
    }

    /**
     * Options for AsyncListWithSkipTake.
     * Extends AsyncListBase.Options - getItemId is inherited from there.
     */
    export interface AsyncListOptions<T> extends Omit<AsyncListBase.Options<T, AsyncListStructure<T>>, 'initialValue' | 'fetchFunction' | 'concurrentFetchingAction'> {
        fetchFunction: (skip: number, take: number | undefined, hasTotalCount: boolean) => Promise<FetchResult<T> | FetchResultWithNullableTotalCount<T>>;
        defaultTake?: number;
        /**
         * Maximum number of items that can be fetched in a single request.
         * Used during refresh to batch items efficiently when the backend has
         * a page size limit smaller than the total items to refresh.
         * Defaults to `defaultTake` if not specified.
         */
        maxFetchBatchSize?: number;
        /**
         * Important: Pass initialItems only in case that you know exactly what they are going to be
         * if the fetchFunction was called with skip = 0. This is because the async list will skip fetching
         * as much items as in the initialItems array.
         */
        initialItems?: T[];
    }
}

export class AsyncListWithSkipTake<T>
    extends AsyncListBase<T, AsyncListWithSkipTake.AsyncListStructure<T>> {

    private readonly _fetchFunction: AsyncListWithSkipTake.AsyncListOptions<T>['fetchFunction'];
    private readonly _defaultTake: number;
    private readonly _maxFetchBatchSize: number;

    constructor(options: AsyncListWithSkipTake.AsyncListOptions<T>) {

        const {
            fetchFunction,
            defaultTake = 10,
            maxFetchBatchSize,
            getItemId,
            initialItems = []
        } = options;

        // Store references for use in refresh()
        // Note: These are assigned after super() call due to TypeScript requirements

        super({
            getItemId,
            initialValue: {
                items: [],
                totalCount: undefined,
                cursor: 0,
                hasMore: true
            },
            concurrentFetchingAction: (param) => {
                if (param === undefined) {
                    return AsyncCollectionConcurrentFetchingAction.skipAndWaitForPendingRequestCompletion();
                } else {
                    return AsyncCollectionConcurrentFetchingAction.serializeAfterPendingRequestCompletion();
                }
            },
            fetchFunction: async (context) => {

                const prevValue = context.currentValue;

                if (prevValue.cursor >= (prevValue.totalCount ?? Infinity)) {
                    return context.skip();
                }

                const takeCount = context.fetchParam ?? defaultTake;

                const result = await fetchFunction(
                    prevValue.cursor,
                    takeCount === Infinity ? undefined : takeCount,
                    prevValue.totalCount !== undefined
                );

                const fetchedItems = result.results;
                const nextStartingItemIndex = prevValue.cursor + fetchedItems.length;

                const updatedTotalCount = result.count ?? prevValue.totalCount;

                return {
                    totalCount: updatedTotalCount,
                    items: [...prevValue.items, ...fetchedItems],
                    cursor: nextStartingItemIndex,
                    hasMore: updatedTotalCount !== undefined ? nextStartingItemIndex < updatedTotalCount : true
                }
            }
        });

        if (initialItems.length > 0) {
            this.currentValue = {
                items: initialItems,
                totalCount: undefined,
                cursor: initialItems.length,
                hasMore: true
            }
        }

        this._fetchFunction = fetchFunction;
        this._defaultTake = defaultTake;
        this._maxFetchBatchSize = maxFetchBatchSize ?? defaultTake;
    }

    getFetchedItemsCount () {
        return this.getItems().length;
    }

    async ensureInitialFetch () {
        const {
            hasMore,
            items
        } = super.getValue();

        if (hasMore && items.length === 0) {
            await this.triggerFetch();
        }
    }

    async ensureFetchedItems (itemsCount: number) {
        const {
            hasMore,
            items
        } = super.getValue();

        if (hasMore && items.length < itemsCount) {
            const result = await this.triggerFetch(itemsCount - items.length);

            return arraySkipTake(result.items, 0, itemsCount);
        } else {
            return arraySkipTake(items, 0, itemsCount);
        }
    }

    getItems () {
        return super.getValue().items;
    }

    updateItems (itemUpdateFunc: (item: T) => T) {

        const updatedItems = this.currentValue.items.map(itemUpdateFunc);
        if (!arrayEqual(this.currentValue.items, updatedItems)) {
            this.handleUpdate({
                ...this.currentValue,
                items: updatedItems
            });
        }
    }

    handleItemsRemoval (removedItemsCount: number, conditionFunction: (item: T) => boolean) {

        const prevItems = this.currentValue.items;
        const updatedItems = prevItems.filter(item => !conditionFunction(item));
        const fetchedRemovedItemsCount = prevItems.length - updatedItems.length;

        if (fetchedRemovedItemsCount !== removedItemsCount) {
            this.reset()
        } else {
            const prevTotalCount = this.currentValue.totalCount;
            const prevCursor = this.currentValue.cursor;

            this.handleUpdate({
                items: updatedItems,
                totalCount: prevTotalCount !== undefined ? prevTotalCount - removedItemsCount : undefined,
                cursor: prevCursor - fetchedRemovedItemsCount,
                // We'll leave the `hasMore` value as is to avoid mistakes.
                hasMore: this.currentValue.hasMore
            });
        }
    }

    getTotalCount () {
        return super.getValue().totalCount;
    }

    hasMore () {
        return super.getValue().hasMore;
    }

    async fetchAllItems () {
        return (await super.triggerFetch(Infinity)).items;
    }

    public supportsRefresh(): boolean {
        return true;
    }

    /**
     * Refreshes the list by re-fetching items from the beginning without showing a loading state.
     * 
     * - Fetches the same number of items currently loaded (or `_defaultTake` if none loaded)
     * - Iterates in chunks of `_maxFetchBatchSize` to respect backend page limits
     * - Deduplication is handled automatically by processValueAfterFetch() when getItemId is provided
     * - Items remain visible during the refresh (no intermediate empty state)
     * - If multiple refreshes are triggered, only the last one updates the state
     * - Errors are swallowed to keep the existing data visible
     * @param refreshOperation The refresh operation promise to check if still current
     */
    protected async refreshInternal(refreshOperation: Promise<void>): Promise<void> {
        const targetItemCount = Math.max(this.getFetchedItemsCount(), this._defaultTake);

        try {
            let allItems: T[] = [];
            let cursor = 0;
            let totalCount: number | undefined;

            // Iterate in chunks of _maxFetchBatchSize
            while (allItems.length < targetItemCount) {
                const take = Math.min(this._maxFetchBatchSize, targetItemCount - allItems.length);
                const result = await this._fetchFunction(cursor, take, totalCount !== undefined);

                allItems = [...allItems, ...result.results];
                totalCount = result.count ?? totalCount;
                cursor += result.results.length;

                // Stop if no more items available
                if (result.results.length < take || (totalCount !== undefined && cursor >= totalCount)) {
                    break;
                }
            }

            // Deduplication is handled automatically by processValueAfterFetch() via handleUpdateFromRefresh()
            this.handleUpdateFromRefresh(refreshOperation, {
                items: allItems,
                cursor: allItems.length,
                totalCount: totalCount,
                hasMore: totalCount !== undefined ? allItems.length < totalCount : true
            });
        } catch {
            // Swallow errors to keep existing data visible
        }
    }
}

export function asyncListWithSkipTakeCreate<T> (options: AsyncListWithSkipTake.AsyncListOptions<T>) : AsyncListWithSkipTake<T> {
    return new AsyncListWithSkipTake<T>(options);
}

const asyncListWithSkipTakeUnknown = evaluateFunction(() => {

    const infiniteDeferred = new Deferred<AsyncListWithSkipTake.FetchResult<any>>();

    return new AsyncListWithSkipTake({
        fetchFunction: async () => {
            return infiniteDeferred.promise;
        }
    })
})

export function asyncListWithSkipTakeCreateUnknown<T> () : AsyncListWithSkipTake<T> {
    return asyncListWithSkipTakeUnknown;
}

export function asyncListWithSkipTakeCreateForLocalDataSource<T> (items: T[]) {
    return new AsyncListWithSkipTake({
        fetchFunction: async (skip: number, take?: number) => {
            return {
                results: items.slice(skip, take !== undefined ? skip + take : undefined),
                count: items.length
            }
        }
    })
}